import { useEffect, useState } from "react";
import { workflowApi, type WorkflowStats } from "@/lib/api";

export default function WorkflowWidgets() {
  const [stats, setStats] = useState<WorkflowStats | null>(null);

  useEffect(() => {
    workflowApi.stats().then((res) => setStats(res.data)).catch(() => setStats(null));
  }, []);

  if (!stats) return null;

  const waitingTotal = stats.waiting_for_approval.scm + stats.waiting_for_approval.management + stats.waiting_for_approval.cfo_hod;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
      {/* Contracts In Renewal */}
      <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-foreground-800 text-sm font-semibold">Contracts In Renewal</p>
          <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center">
            <i className="ri-refresh-line text-primary-500 text-base"></i>
          </div>
        </div>
        <div className="flex items-end justify-between mb-4">
          <div>
            <p className="text-foreground-900 text-3xl font-bold">{stats.total_active_workflows}</p>
            <p className="text-foreground-500 text-xs mt-1">Total Active Workflows</p>
          </div>
          <div className="text-right">
            <p className="text-accent-600 text-xl font-bold">{waitingTotal}</p>
            <p className="text-foreground-500 text-xs mt-1">Waiting For Approval</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 pt-3 border-t border-background-100">
          <div className="text-center">
            <p className="text-foreground-800 text-sm font-semibold">{stats.waiting_for_approval.scm}</p>
            <p className="text-foreground-400 text-[10px] uppercase tracking-wider mt-0.5">SCM</p>
          </div>
          <div className="text-center">
            <p className="text-foreground-800 text-sm font-semibold">{stats.waiting_for_approval.management}</p>
            <p className="text-foreground-400 text-[10px] uppercase tracking-wider mt-0.5">Management</p>
          </div>
          <div className="text-center">
            <p className="text-foreground-800 text-sm font-semibold">{stats.waiting_for_approval.cfo_hod}</p>
            <p className="text-foreground-400 text-[10px] uppercase tracking-wider mt-0.5">CFO/HOD</p>
          </div>
        </div>
      </div>

      {/* Workflow Progress */}
      <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-foreground-800 text-sm font-semibold">Workflow Progress</p>
          <div className="w-9 h-9 rounded-lg bg-secondary-50 flex items-center justify-center">
            <i className="ri-bar-chart-2-line text-secondary-500 text-base"></i>
          </div>
        </div>
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-foreground-500 text-xs">Average Completion</span>
            <span className="text-foreground-800 text-xs font-semibold">{stats.average_completion_percentage}%</span>
          </div>
          <div className="w-full h-2 bg-background-100 rounded-full overflow-hidden">
            <div className="h-full bg-secondary-500 rounded-full transition-all duration-500" style={{ width: `${stats.average_completion_percentage}%` }}></div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 pt-3 border-t border-background-100">
          <div>
            <p className={`text-lg font-bold ${stats.overdue_workflow_count > 0 ? "text-accent-600" : "text-foreground-800"}`}>{stats.overdue_workflow_count}</p>
            <p className="text-foreground-500 text-xs mt-0.5">Overdue</p>
          </div>
          <div>
            <p className="text-foreground-800 text-lg font-bold">{stats.completed_this_month}</p>
            <p className="text-foreground-500 text-xs mt-0.5">Completed This Month</p>
          </div>
        </div>
      </div>
    </div>
  );
}
